import {
  eachDayOfInterval,
  format,
  getHours,
  isWithinInterval,
  startOfWeek,
} from 'date-fns';

export type CallAnalyticsRecord = {
  id: string;
  type?: string | null;
  callType?: string | null;
  call_type?: string | null;
  createdAt?: string | null;
  created_at?: string | null;
  employee_id?: string | null;
  voicemail?: boolean | null;
  payload?: Record<string, unknown> | null;
  employee?: {
    id?: string | null;
    name?: string | null;
    surname?: string | null;
  } | null;
  notes?: Array<{
    id: string;
    data?: {
      id?: string | null;
      title?: string | null;
      content?: string | null;
      user?: string | null;
      createdAt?: string | null;
      updatedAt?: string | null;
    } | null;
  }> | null;
};

export type CallDirectionFilter = 'all' | 'incoming' | 'outgoing' | 'missed';
export type CallGranularity = 'hour' | 'day' | 'week';

export const CALL_CHANNEL_ORDER = [
  'sales',
  'support',
  'models',
  'general',
  'untyped',
  'outgoing',
] as const;

export type CallAnalyticsSummary = {
  totalCalls: number;
  incomingCalls: number;
  outgoingCalls: number;
  missedCalls: number;
  totalDurationSeconds: number;
  averageDurationSeconds: number;
  byPerson: Array<{
    id: string;
    name: string;
    calls: number;
    incomingCalls: number;
    outgoingCalls: number;
    durationSeconds: number;
    missedCalls: number;
  }>;
  byChannel: Array<{
    id: string;
    name: string;
    calls: number;
    incomingCalls: number;
    outgoingCalls: number;
    durationSeconds: number;
    missedCalls: number;
  }>;
};

export type CallChartPoint = {
  bucket: string;
  bucketOrder: number;
  count: number;
  durationSeconds: number;
  series: string;
};

const HOUR_LABELS = Array.from({ length: 24 }, (_, hour) => ({
  hour,
  label: `${String(hour).padStart(2, '0')}:00`,
}));

export function getCallCreatedAt(call: CallAnalyticsRecord) {
  const raw = call.createdAt || call.created_at;
  return raw ? new Date(raw) : null;
}

export function getCallDurationSeconds(
  payload?: Record<string, unknown> | null,
) {
  if (!payload) {
    return 0;
  }

  const raw = payload.duration ?? payload.Duration ?? 0;
  const seconds = parseInt(String(raw), 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
}

export function getCallEmployeeId(call: CallAnalyticsRecord) {
  return call.employee_id || call.employee?.id || 'unassigned';
}

export function getCallEmployeeLabel(call: CallAnalyticsRecord) {
  const name = call.employee?.name;
  const surname = call.employee?.surname;

  if (name) {
    return [name, surname].filter(Boolean).join(' ');
  }

  return 'Unassigned';
}

/** Matches incoming call routing in pages/incoming/index.tsx */
export function getCallChannel(call: CallAnalyticsRecord) {
  const callType = call.callType ?? call.call_type;
  const payloadType = call.payload?.type;
  const raw = callType ?? payloadType;

  if (!raw || String(raw).toLowerCase() === 'unknown') {
    return call.type === 'outgoing' ? 'outgoing' : 'untyped';
  }

  return String(raw).toLowerCase();
}

export function getCallChannelLabel(channel: string) {
  const labels: Record<string, string> = {
    sales: 'Sales',
    support: 'Support',
    models: 'Models',
    general: 'General',
    outgoing: 'Outgoing',
    untyped: 'Untyped',
  };

  return labels[channel] || channel.replace(/_/g, ' ');
}

export function listCallAnalyticsChannels(calls: CallAnalyticsRecord[]) {
  const channels = new Map<string, string>();

  for (const call of calls) {
    const id = getCallChannel(call);
    channels.set(id, getCallChannelLabel(id));
  }

  return Array.from(channels.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => {
      const aIndex = CALL_CHANNEL_ORDER.indexOf(
        a.id as (typeof CALL_CHANNEL_ORDER)[number],
      );
      const bIndex = CALL_CHANNEL_ORDER.indexOf(
        b.id as (typeof CALL_CHANNEL_ORDER)[number],
      );
      const normalizedA = aIndex === -1 ? CALL_CHANNEL_ORDER.length : aIndex;
      const normalizedB = bIndex === -1 ? CALL_CHANNEL_ORDER.length : bIndex;

      if (normalizedA !== normalizedB) {
        return normalizedA - normalizedB;
      }

      return a.name.localeCompare(b.name);
    });
}

/** Matches incoming board missed-call styling in pages/incoming/index.tsx */
export function isMissedCall(call: CallAnalyticsRecord) {
  if (call.type !== 'incoming') {
    return false;
  }

  const payload = call.payload || {};
  const callStatus = payload.CallStatus ?? payload.callStatus;
  const hasEmployee = Boolean(call.employee_id || call.employee?.id);

  if (!hasEmployee && call.voicemail) {
    return true;
  }

  if (callStatus === 'no-answer' && !hasEmployee) {
    return true;
  }

  const duration = payload.duration ?? payload.Duration;
  if (duration === 0 || duration === '0') {
    return true;
  }

  return false;
}

export function formatCallDuration(totalSeconds: number) {
  if (totalSeconds <= 0) {
    return '0m';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  return `${seconds}s`;
}

function filterCalls(
  calls: CallAnalyticsRecord[],
  {
    direction,
    employeeId,
    callChannel,
    start,
    end,
  }: {
    direction: CallDirectionFilter;
    employeeId: string;
    callChannel: string;
    start: Date;
    end: Date;
  },
) {
  return calls.filter((call) => {
    const createdAt = getCallCreatedAt(call);
    if (!createdAt || !isWithinInterval(createdAt, { start, end })) {
      return false;
    }

    if (
      direction === 'incoming' &&
      (call.type !== 'incoming' || isMissedCall(call))
    ) {
      return false;
    }

    if (direction === 'outgoing' && call.type !== 'outgoing') {
      return false;
    }

    if (direction === 'missed' && !isMissedCall(call)) {
      return false;
    }

    if (employeeId !== 'all' && getCallEmployeeId(call) !== employeeId) {
      return false;
    }

    if (callChannel !== 'all' && getCallChannel(call) !== callChannel) {
      return false;
    }

    return true;
  });
}

function buildEmptyHourBuckets(series: string) {
  return HOUR_LABELS.map(({ hour, label }) => ({
    bucket: label,
    bucketOrder: hour,
    count: 0,
    durationSeconds: 0,
    series,
  }));
}

function buildEmptyDayBuckets(start: Date, end: Date, series: string) {
  return eachDayOfInterval({ start, end }).map((day) => ({
    bucket: format(day, 'EEE d MMM'),
    bucketOrder: day.getTime(),
    count: 0,
    durationSeconds: 0,
    series,
  }));
}

function buildEmptyWeekBuckets(start: Date, end: Date, series: string) {
  const weeks = new Map<number, Date>();

  for (const day of eachDayOfInterval({ start, end })) {
    const weekStart = startOfWeek(day, { weekStartsOn: 1 });
    weeks.set(weekStart.getTime(), weekStart);
  }

  return Array.from(weeks.values())
    .sort((a, b) => a.getTime() - b.getTime())
    .map((weekStart) => ({
      bucket: `w/c ${format(weekStart, 'd MMM')}`,
      bucketOrder: weekStart.getTime(),
      count: 0,
      durationSeconds: 0,
      series,
    }));
}

function buildEmptyBuckets(
  granularity: CallGranularity,
  start: Date,
  end: Date,
  series: string,
) {
  if (granularity === 'hour') {
    return buildEmptyHourBuckets(series);
  }

  if (granularity === 'week') {
    return buildEmptyWeekBuckets(start, end, series);
  }

  return buildEmptyDayBuckets(start, end, series);
}

function getBucketKey(createdAt: Date, granularity: CallGranularity) {
  if (granularity === 'hour') {
    return `${String(getHours(createdAt)).padStart(2, '0')}:00`;
  }

  if (granularity === 'week') {
    return `w/c ${format(startOfWeek(createdAt, { weekStartsOn: 1 }), 'd MMM')}`;
  }

  return format(createdAt, 'EEE d MMM');
}

export function listCallAnalyticsEmployees(calls: CallAnalyticsRecord[]) {
  const employees = new Map<string, string>();

  for (const call of calls) {
    const id = getCallEmployeeId(call);
    employees.set(id, getCallEmployeeLabel(call));
  }

  return Array.from(employees.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildCallAnalyticsSummary(
  calls: CallAnalyticsRecord[],
  {
    direction,
    employeeId,
    callChannel,
    start,
    end,
  }: {
    direction: CallDirectionFilter;
    employeeId: string;
    callChannel: string;
    start: Date;
    end: Date;
  },
): CallAnalyticsSummary {
  const filtered = filterCalls(calls, {
    direction,
    employeeId,
    callChannel,
    start,
    end,
  });
  const byPersonMap = new Map<
    string,
    {
      id: string;
      name: string;
      calls: number;
      incomingCalls: number;
      outgoingCalls: number;
      durationSeconds: number;
      missedCalls: number;
    }
  >();
  const byChannelMap = new Map<
    string,
    {
      id: string;
      name: string;
      calls: number;
      incomingCalls: number;
      outgoingCalls: number;
      durationSeconds: number;
      missedCalls: number;
    }
  >();

  let totalDurationSeconds = 0;
  let incomingCalls = 0;
  let outgoingCalls = 0;
  let missedCalls = 0;

  for (const call of filtered) {
    const durationSeconds = getCallDurationSeconds(call.payload);
    totalDurationSeconds += durationSeconds;

    if (isMissedCall(call)) {
      missedCalls += 1;
    } else if (call.type === 'incoming') {
      incomingCalls += 1;
    } else if (call.type === 'outgoing') {
      outgoingCalls += 1;
    }

    const personId = getCallEmployeeId(call);
    const existing = byPersonMap.get(personId) || {
      id: personId,
      name: getCallEmployeeLabel(call),
      calls: 0,
      incomingCalls: 0,
      outgoingCalls: 0,
      durationSeconds: 0,
      missedCalls: 0,
    };

    existing.calls += 1;
    existing.durationSeconds += durationSeconds;
    if (isMissedCall(call)) {
      existing.missedCalls += 1;
    } else if (call.type === 'incoming') {
      existing.incomingCalls += 1;
    } else if (call.type === 'outgoing') {
      existing.outgoingCalls += 1;
    }
    byPersonMap.set(personId, existing);

    const channelId = getCallChannel(call);
    const channelExisting = byChannelMap.get(channelId) || {
      id: channelId,
      name: getCallChannelLabel(channelId),
      calls: 0,
      incomingCalls: 0,
      outgoingCalls: 0,
      durationSeconds: 0,
      missedCalls: 0,
    };

    channelExisting.calls += 1;
    channelExisting.durationSeconds += durationSeconds;
    if (isMissedCall(call)) {
      channelExisting.missedCalls += 1;
    } else if (call.type === 'incoming') {
      channelExisting.incomingCalls += 1;
    } else if (call.type === 'outgoing') {
      channelExisting.outgoingCalls += 1;
    }
    byChannelMap.set(channelId, channelExisting);
  }

  const totalCalls = filtered.length;

  return {
    totalCalls,
    incomingCalls,
    outgoingCalls,
    missedCalls,
    totalDurationSeconds,
    averageDurationSeconds:
      totalCalls > 0 ? Math.round(totalDurationSeconds / totalCalls) : 0,
    byPerson: Array.from(byPersonMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    ),
    byChannel: Array.from(byChannelMap.values()).sort((a, b) => {
      const aIndex = CALL_CHANNEL_ORDER.indexOf(
        a.id as (typeof CALL_CHANNEL_ORDER)[number],
      );
      const bIndex = CALL_CHANNEL_ORDER.indexOf(
        b.id as (typeof CALL_CHANNEL_ORDER)[number],
      );
      const normalizedA = aIndex === -1 ? CALL_CHANNEL_ORDER.length : aIndex;
      const normalizedB = bIndex === -1 ? CALL_CHANNEL_ORDER.length : bIndex;

      if (normalizedA !== normalizedB) {
        return normalizedA - normalizedB;
      }

      return a.name.localeCompare(b.name);
    }),
  };
}

function populateBucketsFromCalls(
  buckets: CallChartPoint[],
  bucketIndex: Map<string, number>,
  callList: CallAnalyticsRecord[],
  granularity: CallGranularity,
) {
  for (const call of callList) {
    const createdAt = getCallCreatedAt(call);
    if (!createdAt) {
      continue;
    }

    const bucketKey = getBucketKey(createdAt, granularity);
    const index = bucketIndex.get(bucketKey);
    if (index === undefined) {
      continue;
    }

    buckets[index].count += 1;
    buckets[index].durationSeconds += getCallDurationSeconds(call.payload);
  }
}

export function buildCallVolumeChartData(
  calls: CallAnalyticsRecord[],
  {
    direction,
    employeeId,
    callChannel,
    granularity,
    splitByPerson,
    start,
    end,
  }: {
    direction: CallDirectionFilter;
    employeeId: string;
    callChannel: string;
    granularity: CallGranularity;
    splitByPerson: boolean;
    start: Date;
    end: Date;
  },
): CallChartPoint[] {
  const filtered = filterCalls(calls, {
    direction,
    employeeId,
    callChannel,
    start,
    end,
  });

  if (direction === 'all' && !splitByPerson) {
    const seriesDefinitions: Array<{
      label: string;
      matches: (call: CallAnalyticsRecord) => boolean;
    }> = [
      {
        label: 'Received',
        matches: (call) => call.type === 'incoming' && !isMissedCall(call),
      },
      {
        label: 'Missed',
        matches: (call) => isMissedCall(call),
      },
      {
        label: 'Made',
        matches: (call) => call.type === 'outgoing',
      },
    ];
    const chartData: CallChartPoint[] = [];

    for (const { label, matches } of seriesDefinitions) {
      const seriesCalls = filtered.filter(matches);
      const buckets = buildEmptyBuckets(granularity, start, end, label);
      const bucketIndex = new Map(
        buckets.map((bucket, index) => [bucket.bucket, index]),
      );

      populateBucketsFromCalls(buckets, bucketIndex, seriesCalls, granularity);
      chartData.push(...buckets);
    }

    return chartData.sort(
      (a, b) =>
        a.bucketOrder - b.bucketOrder || a.series.localeCompare(b.series),
    );
  }

  if (!splitByPerson || employeeId !== 'all') {
    const series =
      direction === 'missed'
        ? 'Missed'
        : direction === 'incoming'
          ? 'Received'
          : direction === 'outgoing'
            ? 'Made'
            : employeeId === 'all'
              ? 'All team'
              : filtered[0]
                ? getCallEmployeeLabel(filtered[0])
                : 'Selected member';
    const buckets = buildEmptyBuckets(granularity, start, end, series);
    const bucketIndex = new Map(
      buckets.map((bucket, index) => [bucket.bucket, index]),
    );

    for (const call of filtered) {
      const createdAt = getCallCreatedAt(call);
      if (!createdAt) {
        continue;
      }

      const bucketKey = getBucketKey(createdAt, granularity);
      const index = bucketIndex.get(bucketKey);
      if (index === undefined) {
        continue;
      }

      buckets[index].count += 1;
      buckets[index].durationSeconds += getCallDurationSeconds(call.payload);
    }

    return buckets;
  }

  const employees = listCallAnalyticsEmployees(filtered);
  const chartData: CallChartPoint[] = [];

  for (const employee of employees) {
    const employeeCalls = filtered.filter(
      (call) => getCallEmployeeId(call) === employee.id,
    );
    const buckets = buildEmptyBuckets(granularity, start, end, employee.name);
    const bucketIndex = new Map(
      buckets.map((bucket, index) => [bucket.bucket, index]),
    );

    populateBucketsFromCalls(buckets, bucketIndex, employeeCalls, granularity);

    chartData.push(...buckets);
  }

  return chartData.sort(
    (a, b) => a.bucketOrder - b.bucketOrder || a.series.localeCompare(b.series),
  );
}

export function formatAnalyticsPeriodLabel(start: Date, end: Date) {
  return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
}
